// Reading a class-note back into the "Đánh giá tiết học" form. Split out of the editor so the
// round-trip - the part that silently duplicated text - is testable without JSX.

import type { QltClassNoteData } from '../../api/types';
import { composeSentence, orderItems, restoreChecklist, type ChecklistText, type SentenceRule } from './checklistText.ts';

export interface ClassMarkBand {
  id: string;
  badge: string;
  label: string;
  mark: number;
}

// MOCK M5: no endpoint serves the band catalog and class-note.data.classMark has no enum.
export const CLASS_MARK_BANDS: ClassMarkBand[] = [
  { id: 'excellent', badge: '10-9', label: 'Rất tốt', mark: 10 },
  { id: 'good', badge: '9-8', label: 'Tốt', mark: 9 },
  { id: 'fair', badge: '8-7', label: 'Cần cố gắng', mark: 8 },
  { id: 'weak', badge: '7-6', label: 'Chưa tốt', mark: 7 },
  { id: 'poor', badge: '6-5', label: 'Kém', mark: 6 },
  { id: 'bad', badge: 'Dưới 5', label: 'Rất kém', mark: 4 },
];

// MOCK M5: verbatim from the "Nhận xét lớp" export - the improvement list, for 8-7 and below.
export const CLASS_COMMENT_IMPROVE = [
  'Mất tập trung',
  'Chưa hoàn thành nhiệm vụ',
  'Không tham gia',
  'Nề nếp kém',
  'Thiếu động lực',
  'Khó khăn trong việc quản lý thời gian',
];

// MOCK M5: the export and lms-fe have no positive list; 'Hăng hái phát biểu' is lms-fe's, the rest mirror the list above.
export const CLASS_COMMENT_POSITIVE = [
  'Tập trung nghe giảng',
  'Hoàn thành tốt nhiệm vụ',
  'Hăng hái phát biểu',
  'Tích cực tham gia',
  'Nề nếp tốt',
];

/** Every preset, for reading back a flat classComment whatever band it was written under. */
export const CLASS_COMMENT_PRESETS = CLASS_COMMENT_POSITIVE.concat(CLASS_COMMENT_IMPROVE);

// presets carry no subject, so the generated sentence opens with "Lớp"
export const CLASS_COMMENT_RULE: SentenceRule = { subject: 'Lớp', order: CLASS_COMMENT_PRESETS };

const POSITIVE_BANDS = ['excellent', 'good'];

/** The comment chips a band offers; with no band picked yet, all of them. */
export function commentPresetsFor(bandId: string): string[] {
  if (!bandById(bandId)) return CLASS_COMMENT_PRESETS;
  return POSITIVE_BANDS.indexOf(bandId) >= 0 ? CLASS_COMMENT_POSITIVE : CLASS_COMMENT_IMPROVE;
}

/** Ticked comments that still fit a newly picked band; the rest are unticked. */
export function tagsForBand(tags: string[], bandId: string): string[] {
  const allowed = commentPresetsFor(bandId);
  return tags.filter((tag) => allowed.indexOf(tag) >= 0);
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}

export function bandById(id: string): ClassMarkBand | undefined {
  for (let i = 0; i < CLASS_MARK_BANDS.length; i += 1) if (CLASS_MARK_BANDS[i].id === id) return CLASS_MARK_BANDS[i];
  return undefined;
}

// band id, then the badge seeded records store instead of the id, then the numeric mark it lands in;
// a band the mark no longer agrees with was left behind by a QLT web edit of classMark alone
export function bandIdOf(data?: QltClassNoteData): string {
  if (!data) return '';
  const stored = asText(data.classMarkBand);
  const mark = data.classMark;
  for (let i = 0; i < CLASS_MARK_BANDS.length; i += 1) {
    const band = CLASS_MARK_BANDS[i];
    if ((band.id === stored || band.badge === stored) && (typeof mark !== 'number' || mark === band.mark)) return band.id;
  }
  if (typeof mark !== 'number') return '';
  for (let i = 0; i < CLASS_MARK_BANDS.length; i += 1) if (mark >= CLASS_MARK_BANDS[i].mark) return CLASS_MARK_BANDS[i].id;
  return CLASS_MARK_BANDS[CLASS_MARK_BANDS.length - 1].id;
}

export function classMarkLabel(data?: QltClassNoteData): string {
  const band = bandById(bandIdOf(data));
  return band ? band.label : '';
}

const SEPARATOR = '; ';

function flatComment(tags: readonly string[], note: string): string {
  return tags.concat(note ? [note] : []).join(SEPARATOR);
}

/** Splits a record into ticked presets and leftover free text. Without this the already-joined
    classComment lands in the note box whole and every save re-appends the presets inside it. */
export function hydrateComment(data?: QltClassNoteData): { tags: string[]; note: string } {
  if (!data) return { tags: [], note: '' };
  const comment = asText(data.classComment).trim();
  // structured fields count only while they still spell classComment: QLT web rewrites classComment alone
  if (Array.isArray(data.classCommentTags)) {
    const structured = { tags: asList(data.classCommentTags), note: asText(data.classCommentNote).trim() };
    if (flatComment(structured.tags, structured.note) === comment) return structured;
  }
  const tags: string[] = [];
  const rest: string[] = [];
  comment.split(';').forEach((part) => {
    const value = part.trim();
    if (!value) return;
    if (CLASS_COMMENT_PRESETS.indexOf(value) < 0) rest.push(value);
    else if (tags.indexOf(value) < 0) tags.push(value);
  });
  return { tags, note: rest.join(SEPARATOR) };
}

/** The comment box and its ticks. A record this editor saved keeps its text as is; an older "a; b; note"
    record, or one QLT web rewrote, becomes the generated sentence followed by its free text. */
export function hydrateCommentText(data?: QltClassNoteData): ChecklistText {
  const comment = asText(data?.classComment).trim();
  if (data && Array.isArray(data.classCommentTags) && asText(data.classCommentText) === comment) {
    return restoreChecklist(CLASS_COMMENT_RULE, asList(data.classCommentTags), comment);
  }
  const legacy = hydrateComment(data);
  if (legacy.tags.length === 0) return { ticks: [], text: comment, owned: [] };
  const sentence = composeSentence(CLASS_COMMENT_RULE, legacy.tags);
  return restoreChecklist(CLASS_COMMENT_RULE, legacy.tags, legacy.note ? `${sentence} ${legacy.note}` : sentence);
}

/** What the editor writes back: classComment is the box text; classCommentText marks it as ours so a QLT web edit is spotted. */
export function commentPayload(ticks: string[], text: string): Pick<QltClassNoteData, 'classComment' | 'classCommentText' | 'classCommentTags' | 'classCommentNote'> {
  const trimmed = text.trim();
  // classCommentNote is the old split-out free text; undefined drops it from the saved data
  return { classComment: trimmed, classCommentText: trimmed, classCommentTags: orderItems(CLASS_COMMENT_PRESETS, ticks), classCommentNote: undefined };
}
